/**
 * Airline display names → IATA codes. Plain JS so the browser
 * (src/utils/airlineBranding.js re-exports it), the Express server and the
 * edge functions (Deno) resolve "Delta" / "Delta Air Lines" the same way.
 * Moved here from src/utils/airlineBranding.js (2026-10-28) so the fare-seller
 * check in fare-option-integrity.js can use it at checkout.
 */

/**
 * Curated display-name → IATA map. Keys are normalized (lowercase, ascii,
 * collapsed spaces). Lookup tries the exact normalized name first, then the
 * name with a trailing legal suffix ("Airlines", "Air Lines", "Airways",
 * "Air", "Aviation") stripped — so "Delta Air Lines", "Delta Airlines" and
 * "Delta" all resolve to DL without tripling the table.
 */
export const AIRLINE_NAME_TO_IATA = Object.freeze({
  // US majors + LCCs
  american: 'AA',
  delta: 'DL',
  united: 'UA',
  southwest: 'WN',
  alaska: 'AS',
  jetblue: 'B6',
  hawaiian: 'HA',
  spirit: 'NK',
  frontier: 'F9',
  allegiant: 'G4',
  'sun country': 'SY',
  breeze: 'MX',
  avelo: 'XP',
  'silver airways': '3M',
  'cape air': '9K',
  contour: 'LF',
  'virgin america': 'VX',
  // Canada / Mexico / Central & South America / Caribbean
  'air canada': 'AC',
  westjet: 'WS',
  porter: 'PD',
  aeromexico: 'AM',
  volaris: 'Y4',
  'viva aerobus': 'VB',
  vivaaerobus: 'VB',
  copa: 'CM',
  avianca: 'AV',
  latam: 'LA',
  azul: 'AD',
  gol: 'G3',
  'aerolineas argentinas': 'AR',
  'caribbean airlines': 'BW',
  bahamasair: 'UP',
  'cayman airways': 'KX',
  intercaribbean: 'JY',
  winair: 'WM',
  'windward islands airways': 'WM',
  // Europe
  'british airways': 'BA',
  'virgin atlantic': 'VS',
  'air france': 'AF',
  klm: 'KL',
  'klm royal dutch': 'KL',
  lufthansa: 'LH',
  swiss: 'LX',
  'swiss international': 'LX',
  austrian: 'OS',
  brussels: 'SN',
  iberia: 'IB',
  'aer lingus': 'EI',
  ita: 'AZ',
  'tap air portugal': 'TP',
  'tap portugal': 'TP',
  tap: 'TP',
  sas: 'SK',
  scandinavian: 'SK',
  finnair: 'AY',
  icelandair: 'FI',
  play: 'OG',
  'norse atlantic': 'N0',
  norse: 'N0',
  ryanair: 'FR',
  easyjet: 'U2',
  'wizz air': 'W6',
  wizzair: 'W6',
  vueling: 'VY',
  norwegian: 'DY',
  'norwegian air shuttle': 'DY',
  aegean: 'A3',
  lot: 'LO',
  'lot polish': 'LO',
  'air europa': 'UX',
  condor: 'DE',
  eurowings: 'EW',
  pegasus: 'PC',
  // Middle East / Africa / Turkey
  turkish: 'TK',
  emirates: 'EK',
  qatar: 'QR',
  etihad: 'EY',
  saudia: 'SV',
  'riyadh air': 'RX',
  'el al': 'LY',
  'royal air maroc': 'AT',
  egyptair: 'MS',
  ethiopian: 'ET',
  'kenya airways': 'KQ',
  flydubai: 'FZ',
  'air arabia': 'G9',
  'oman air': 'WY',
  'gulf air': 'GF',
  'kuwait airways': 'KU',
  'royal jordanian': 'RJ',
  'middle east airlines': 'ME',
  // Asia / Pacific
  singapore: 'SQ',
  'cathay pacific': 'CX',
  cathay: 'CX',
  'japan airlines': 'JL',
  jal: 'JL',
  ana: 'NH',
  'all nippon': 'NH',
  'korean air': 'KE',
  asiana: 'OZ',
  'air china': 'CA',
  'china eastern': 'MU',
  'china southern': 'CZ',
  'eva air': 'BR',
  'china airlines': 'CI',
  'philippine airlines': 'PR',
  'cebu pacific': '5J',
  'vietnam airlines': 'VN',
  thai: 'TG',
  malaysia: 'MH',
  garuda: 'GA',
  'garuda indonesia': 'GA',
  'air india': 'AI',
  indigo: '6E',
  vistara: 'UK',
  srilankan: 'UL',
  'bangkok airways': 'PG',
  scoot: 'TR',
  airasia: 'AK',
  jetstar: 'JQ',
  zipair: 'ZG',
  starlux: 'JX',
  'hainan airlines': 'HU',
  qantas: 'QF',
  'virgin australia': 'VA',
  'air new zealand': 'NZ',
  'fiji airways': 'FJ',
  'air tahiti nui': 'TN',
});

/** Lowercase, strip diacritics, collapse whitespace. Exported so callers can
 *  detect "did the admin actually change the airline" without spelling drift. */
export function normalizeAirlineName(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Resolve a display name ("Delta", "Delta Air Lines") to its IATA code, or null. */
export function resolveAirlineIata(name) {
  const normalized = normalizeAirlineName(name);
  if (!normalized) return null;
  if (AIRLINE_NAME_TO_IATA[normalized]) return AIRLINE_NAME_TO_IATA[normalized];
  const stripped = normalized.replace(/\s+(airlines?|air\s?lines?|airways?|aviation|air)$/, '');
  if (stripped !== normalized && AIRLINE_NAME_TO_IATA[stripped]) {
    return AIRLINE_NAME_TO_IATA[stripped];
  }
  return null;
}
